import type { AppBaseInfo } from '@affine/core/modules/streamlain/hooks/use-workspace-app-bases';
import { isDatabaseTrashBlocked } from '@streamlain/database-identity';

export interface ManagedDatabaseOwner {
  label: string;
  openLabel: string;
  open: () => void;
}

export interface ManagedDatabaseProtectionInput {
  schemaLocked?: boolean;
  systemManaged?: boolean;
  managedBy?: string;
}

export function isManagedDatabaseProtected(
  input: ManagedDatabaseProtectionInput | null | undefined
): boolean {
  return input ? isDatabaseTrashBlocked(input) : false;
}

export function resolveAppBaseDatabaseOwner(input: {
  blockId: string;
  docId: string;
  databaseId?: string | null;
  appBases: AppBaseInfo[] | undefined;
  openAppBase: (appBaseId: string) => void;
}): ManagedDatabaseOwner | null {
  for (const appBase of input.appBases ?? []) {
    if (!appBaseConfigContainsDatabase(appBase.config, input)) continue;
    return {
      label: `App "${appBase.title}"`,
      openLabel: 'App öffnen',
      open: () => input.openAppBase(appBase.id),
    };
  }
  return null;
}

export function describeManagedDatabaseOwner(input: {
  managedBy?: string;
  fallbackDocTitle?: string;
  explicitOwner?: ManagedDatabaseOwner | null;
  openSourceDoc?: (() => void) | null;
}): ManagedDatabaseOwner | null {
  if (input.explicitOwner) return input.explicitOwner;
  if (!input.managedBy && !input.openSourceDoc) return null;

  const label = input.managedBy
    ? `System "${input.managedBy}"`
    : input.fallbackDocTitle
      ? `Dokument "${input.fallbackDocTitle}"`
      : 'System';

  return {
    label,
    openLabel: 'Quelle öffnen',
    open: () => input.openSourceDoc?.(),
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function appBaseConfigContainsDatabase(
  config: unknown,
  input: { blockId: string; docId: string; databaseId?: string | null }
): boolean {
  const databaseBindings = asRecord(asRecord(config)?.databaseBindings);
  if (!databaseBindings) return false;

  for (const value of Object.values(databaseBindings)) {
    const binding = asRecord(value);
    const placement = asRecord(binding?.placement);
    if (!binding || !placement) continue;
    if (binding.status !== undefined && binding.status !== 'active') continue;
    if (placement.docId !== input.docId) continue;
    if (placement.blockId === input.blockId) return true;
    if (input.databaseId && binding.databaseId === input.databaseId) {
      return true;
    }
  }
  return false;
}
